import type { RenderableFormItem } from "./form-types";
import type { HeroVariantStyles } from "./hero-variant-styles";
import type { StepInputRendererProps } from "./StepInputRenderer";

import { cn } from "@homeinstead/ui";
import { StepInputRenderer } from "./StepInputRenderer";

// Bottom-only padding values per size — selected by designer Cam:
//   sm: 24px  md: 40px  lg: 56px
const ITEM_BOTTOM_PADDING_CLASSES = {
  lg: "pb-14", // 56px
  md: "pb-10", // 40px
  "md-xl-responsive": "pb-10 md:pb-24", // 40px → 96px
  sm: "pb-6", // 24px
  xl: "pb-24", // 96px
} as const;

type ItemBottomPadding = keyof typeof ITEM_BOTTOM_PADDING_CLASSES;

function getItemBottomPaddingClass(padding: string | null | undefined): string {
  if (padding && padding in ITEM_BOTTOM_PADDING_CLASSES) {
    return ITEM_BOTTOM_PADDING_CLASSES[padding as ItemBottomPadding];
  }
  return "";
}

const CONTENT_MAX_WIDTH_CLASS: Record<string, string> = {
  lg: "md:max-w-lg",
  md: "md:max-w-md",
  none: "",
  sm: "md:max-w-sm",
  xl: "md:max-w-xl",
};

function getContentMaxWidthClass(value: string | null | undefined): string {
  if (value && value in CONTENT_MAX_WIDTH_CLASS) {
    return CONTENT_MAX_WIDTH_CLASS[value];
  }
  return CONTENT_MAX_WIDTH_CLASS.md;
}

function resolveItemVerticalPadding(item: RenderableFormItem): string | null {
  if ("verticalPadding" in item) {
    return (
      (item as RenderableFormItem & { verticalPadding?: string | null })
        .verticalPadding ?? null
    );
  }
  return null;
}

export type LeadFormVisibleItemsProps = {
  contentMaxWidth?: string | null;
  visibleItems: RenderableFormItem[];
  heroVariantStyles?: HeroVariantStyles;
} & Omit<StepInputRendererProps, "input">;

export function LeadFormVisibleItems({
  contentMaxWidth,
  errors,
  hideContinueButton,
  isSubmitting,
  officeRating,
  onContinue,
  onFieldChange,
  onPostalLocationChange,
  onValidateAndContinue,
  phoneNumber,
  placeholderValues,
  stepId,
  stepName,
  stepUserSelection,
  visibleItems,
  heroVariantStyles,
}: LeadFormVisibleItemsProps) {
  if (visibleItems.length === 0) {
    return (
      <p className="text-gray-400 text-sm italic">
        No items configured for this step.
      </p>
    );
  }

  return (
    <div
      className={cn(
        "flex w-full flex-col gap-4 md:min-w-[440px]",
        getContentMaxWidthClass(contentMaxWidth),
      )}
    >
      {visibleItems.map((item) => (
        <div
          className={cn(
            "w-full",
            getItemBottomPaddingClass(resolveItemVerticalPadding(item)),
          )}
          key={item._key}
        >
          <StepInputRenderer
            errors={errors}
            heroVariantStyles={heroVariantStyles}
            hideContinueButton={hideContinueButton}
            input={item}
            isSubmitting={isSubmitting}
            officeRating={officeRating}
            onContinue={onContinue}
            onFieldChange={onFieldChange}
            onPostalLocationChange={onPostalLocationChange}
            onValidateAndContinue={onValidateAndContinue}
            phoneNumber={phoneNumber}
            placeholderValues={placeholderValues}
            stepId={stepId}
            stepName={stepName}
            stepUserSelection={stepUserSelection}
          />
        </div>
      ))}
    </div>
  );
}
